import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'

/**
 * This is the global state for the multiples sites and locales supported in the App.
 *
 * To use the context simply import them into the component requiring context
 * like the below example:
 *
 * import React, {useContext} from 'react'
 * import {MultiSiteContext} from './contexts'
 *
 * export const RootCurrencyLabel = () => {
 *    const {site,locale,urlTemplate} = useContext(MultiSiteContext)
 *    return <div>{site} {locale}</div>
 * }
 *
 * Alternatively you can use the hook provided by us:
 *
 * import {useMultiSite} from './hooks'
 *
 * const {site, locale, buildUrl} = useMultiSite()
 * @type {React.Context<unknown>}
 */
export const ShipToCountryContext = React.createContext()
export const ShipToCountryProvider = ({shipToCountry: initialShipToCountry = {}, children}) => {
    const [shipToCountry, setShipToCountryState] = useState(initialShipToCountry)
    // @TODO adjust on dispatch page + shopper context rework
    const hasInitialShipToCountry =
        initialShipToCountry && Object.keys(initialShipToCountry).length > 0

    const [isReady, setIsReady] = useState(isServer || hasInitialShipToCountry)

    const setShipToCountry = (newCountry) => {
        setShipToCountryState(newCountry)
        setIsReady(true)
    }
    return (
        <ShipToCountryContext.Provider value={{shipToCountry, setShipToCountry, isReady}}>
            {children}
        </ShipToCountryContext.Provider>
    )
}

ShipToCountryProvider.propTypes = {
    children: PropTypes.node.isRequired,
    shipToCountry: PropTypes.shape({
        id: PropTypes.string,
        currency: PropTypes.string
    })
}

/**
 * Holds the SSR delivery-country decision for the hydrated React tree.
 *
 * @type {React.Context<object|null>}
 */
export const DeliveryCountryDecisionContext = React.createContext(null)

/**
 * Provides the SSR delivery-country decision to components that render modals or context state.
 *
 * @component
 * @param {object} props - Component props.
 * @param {object|null} [props.deliveryCountryDecision=null] - Decision created by SSR middleware.
 * @param {React.ReactNode} props.children - Descendant components that consume the decision.
 * @returns {React.ReactElement} Delivery-country decision provider.
 */
export const DeliveryCountryDecisionProvider = ({deliveryCountryDecision = null, children}) => {
    return (
        <DeliveryCountryDecisionContext.Provider value={deliveryCountryDecision}>
            {children}
        </DeliveryCountryDecisionContext.Provider>
    )
}

DeliveryCountryDecisionProvider.propTypes = {
    children: PropTypes.node.isRequired,
    deliveryCountryDecision: PropTypes.object
}

/**
 * Reads the SSR delivery-country decision from React context.
 *
 * @returns {object|null} Delivery-country decision for the current request.
 */
export const useDeliveryCountryDecision = () =>
    React.useContext(DeliveryCountryDecisionContext)

/**
 * Provides a context and provider for managing password-related state within a React application.
 *
 * The `PasswordContext` is used to share the password state across the component tree without
 * having to pass props down manually at every level.
 *
 * The `PasswordProvider` component initializes the password-related state and makes it available
 * to any components that are descendants of this provider.
 *
 * @constant {React.Context} PasswordContext - The context object for password management.
 *
 * @component
 * @param {Object} props - The properties passed to the provider component.
 * @param {React.ReactNode} props.children - The child components that will have access to the context.
 *
 * @returns {React.Element} A React element that provides the password context to its children.
 */
export const PasswordContext = React.createContext()
export const PasswordProvider = ({children}) => {
    const [codeVerifier, setCodeVerifier] = useState(null)

    return (
        <PasswordContext.Provider value={{codeVerifier, setCodeVerifier}}>
            {children}
        </PasswordContext.Provider>
    )
}

PasswordProvider.propTypes = {
    children: PropTypes.node.isRequired
}

/**
 * A hook for managing password state and actions
 * @returns {Object} Checkout data and actions
 */
export const usePasswordContext = () => {
    return React.useContext(PasswordContext)
}

/**
 * Provides a context and provider for managing IP address state in a React application.
 *
 * This module exports two main components:
 * 1. `IPContext`: A React context for sharing the IP address state across the component tree.
 * 2. `IPProvider`: A context provider component that supplies the IP address value to its descendants.
 *
 * The `IPProvider` component expects two props:
 * - `ip` (string, required): The initial IP address value to be shared within the context.
 * - `children` (node, required): The child components that will have access to the context value.
 *
 * Usage:
 * Wrap the `IPProvider` around any component tree that needs access to the IP address context.
 * The `IPContext` can then be consumed using the `useContext` hook in any descendant component.
 */
export const IPContext = React.createContext()
export const IPProvider = ({ip, hasFetched, isIPBlocked, children}) => {
    const [localIsIPBlocked, setIsIPBlocked] = useState(isIPBlocked)
    useEffect(() => {
        setIsIPBlocked(isIPBlocked)
    }, [isIPBlocked])

    return (
        <IPContext.Provider value={{ip, hasFetched, isIPBlocked: localIsIPBlocked, setIsIPBlocked}}>
            {children}
        </IPContext.Provider>
    )
}

IPProvider.propTypes = {
    children: PropTypes.node.isRequired,
    ip: PropTypes.string,
    hasFetched: PropTypes.bool.isRequired,
    isIPBlocked: PropTypes.bool
}

/**
 * ConversionRateProvider is a context provider component that supplies conversion rate fields
 * to its child components. It uses the React Context API to pass down the conversion rate data
 * without having to explicitly pass props through every level of the component tree.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child components that will have access to the conversion rate context.
 * @param {Object} props.conversionRateFields - The conversion rate data that will be provided to the context consumers.
 *
 * @returns {JSX.Element} A provider component that wraps its children with the ConversionRateContext.
 */
const ConversionRateContext = React.createContext()

export const ConversionRateProvider = ({children, conversionRateFields}) => {
    return (
        <ConversionRateContext.Provider value={conversionRateFields}>
            {children}
        </ConversionRateContext.Provider>
    )
}

export const useConversionRate = () => {
    return React.useContext(ConversionRateContext)
}

ConversionRateProvider.propTypes = {
    children: PropTypes.node.isRequired,
    conversionRateFields: PropTypes.shape({
        conversionRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        lcpMultiplier: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        paymentCurrencyCode: PropTypes.string,
        usePriceConverter: PropTypes.bool,
        internationalShipping: PropTypes.bool
    }).isRequired
}

// Create a context for the Newsletter subscription modal and its coresponding state hook
export const NewsletterSubscriptionModalContext = React.createContext()

export const NewsletterSubscriptionModalContextProvider = ({value, children}) => {
    return (
        <NewsletterSubscriptionModalContext.Provider value={value}>
            {children}
        </NewsletterSubscriptionModalContext.Provider>
    )
}

export const useNewsletterSubscriptionModal = () =>
    React.useContext(NewsletterSubscriptionModalContext)

NewsletterSubscriptionModalContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
    value: PropTypes.object.isRequired
}

// Create the context
const ProductContext = React.createContext()

// Hook to use the context
export const useProductContext = () => React.useContext(ProductContext)

// Context provider
export const ProductProvider = ({productData, children}) => {
    return <ProductContext.Provider value={productData}>{children}</ProductContext.Provider>
}

ProductProvider.propTypes = {
    productData: PropTypes.object, // The product data to be provided
    children: PropTypes.node.isRequired // The children components that will consume the context
}

/**
 * Provides a React Context and corresponding Provider component for managing the state of a header banner across the application.
 *
 * @typedef {Object} HeaderBannerStateContext
 * @property {string} headerBannerState - The current state of the header banner.
 * @property {Function} setHeaderBannerState - Function to update the header banner state.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.headerBannerState] - The initial state value for the header banner.
 * @param {React.ReactNode} props.children - The child components that will have access to the context.
 * @returns {React.ReactElement} The provider component that supplies the header banner state and updater function to its descendants.
 *
 * @example
 * <HeaderBannerStateProvider headerBannerState="closed">
 *   <App />
 * </HeaderBannerStateProvider>
 *
 * Descendant components can access and update the header banner state using the HeaderBannerStateContext.
 */
export const HeaderBannerStateContext = React.createContext()
export const HeaderBannerStateProvider = ({
    headerBannerState: initialheaderBannerState = '',
    children
}) => {
    const [headerBannerState, setHeaderBannerState] = useState(initialheaderBannerState)

    return (
        <HeaderBannerStateContext.Provider value={{headerBannerState, setHeaderBannerState}}>
            {children}
        </HeaderBannerStateContext.Provider>
    )
}

HeaderBannerStateProvider.propTypes = {
    children: PropTypes.node.isRequired,
    headerBannerState: PropTypes.string
}
